import { LoadingOutlined } from "@ant-design/icons";
import Checkbox from "antd/lib/checkbox/Checkbox";
import { FC, useEffect, useState } from "react";
import { Col, Input, Row, Select } from "antd";

import {
  ICountry,
  IJobGrade,
  IModal,
  IShortTermPlan,
  IPlanType,
} from "@types";

import { showSuccessPopup, sortedByName } from "@utils";

import {
  ErrorServices,
  useCreateShortTermPlanMutation,
  useFetchAllCompanyJobGradesQuery,
  useFetchCompanyCountriesQuery,
  useFetchShortTermPlanTypesQuery,
  useUpdateShortTermPlanMutation,
  useFetchAllCompanyJobFunctionsQuery,
} from "@services";

import { useFormValidation, useTypedSelector } from "@hooks";
import CustomModal from "@/components/customModel";
import { ALL_GRADES_VALUE, ALL_VALUE } from "@/utils/constant";

const { Option } = Select;

interface IFormValues {
  name: string;
  plan_type_id: null | number;
  grade_ids: number[];
  country_ids?: number[];

  job_function_id: number | null;
  sub_job_function_ids: number[];
}

interface IAddShortTermPlan extends IModal {
  selectedShortTermPlan: IShortTermPlan | null;
  setSelectedShortTermPlan: React.Dispatch<
    React.SetStateAction<IShortTermPlan | null>
  >;
  isEdit?: boolean;
}

const AddShortTermPlan: FC<IAddShortTermPlan> = ({
  isVisible,
  setIsVisible,
  selectedShortTermPlan,
  setSelectedShortTermPlan,
  isEdit,
}) => {
  const { company } = useTypedSelector(state => state.auth?.user!);
  const { id: company_id } = company!;

  const [isGlobal, setIsGlobal] = useState(false);
  const [gradeCountries, setGradeCountries] = useState<ICountry[]>([]);
  const [isAllGrades, setIsAllGrades] = useState(false);
  const [shortTermPlan, setShortTermPlan] =
    useState<IFormValues>({
      name: "",
      plan_type_id: null,
      country_ids: [],
      grade_ids: [],
      job_function_id: null,
      sub_job_function_ids: [],
    });

  const { data: planTypesData, isLoading: isFetchingTypes } =
    useFetchShortTermPlanTypesQuery(null);

  const { data: planTypes } = planTypesData || {};

  const { data: jobGradesData, isLoading: isFetchingGrades } =
    useFetchAllCompanyJobGradesQuery({ company_id });

  const { data: jobGrades } = jobGradesData || {};

  const { data: countriesData, isLoading: isFetchingCountries } =
    useFetchCompanyCountriesQuery({
      company_id,
    });

  const { data: countries } = countriesData || {};

  const { data: companyJobFunctionsData } =
    useFetchAllCompanyJobFunctionsQuery({
      companyId: company_id,
    });

  const jobFunctions = companyJobFunctionsData?.data || [];

  const subJobFunctions =
    jobFunctions.find(
      (item: any) =>
        item.id === shortTermPlan.job_function_id
    )?.sub_job_functions || [];

  const [createShortTermPlan, { isLoading: isCreating }] =
    useCreateShortTermPlanMutation();

  const [updateShortTermPlan, { isLoading: isUpdating }] =
    useUpdateShortTermPlanMutation();


  const validation = useFormValidation(shortTermPlan, v => ({
    name: !v.name,
    plan_type: !v.plan_type_id,
    grade_ids:
      !isAllGrades &&
      (!v.grade_ids || v.grade_ids.length === 0),
    countries: !v.country_ids || v.country_ids.length === 0,
    // job_function_id: !v.job_function_id,
    // sub_job_function_ids:
    //   !v.sub_job_function_ids ||
    //   v.sub_job_function_ids.length === 0,
  }));

  useEffect(() => {
    if (selectedShortTermPlan && isEdit) {
      const {
        grade_obj,
        is_all_grade,
        is_global,
        name,
        countries,
        type,
      } = selectedShortTermPlan;

      const normalizedCountries =
        countries?.map((c: any) => ({
          id: c.id,
          name: c?.name,
        })) || [];

      // FIXED
      const selectedGradeIds =
        grade_obj?.map((grade: any) => grade.id) || [];

      setShortTermPlan({
        name,
        plan_type_id: type?.id,
        grade_ids: selectedGradeIds,
        country_ids: normalizedCountries?.map(c => c.id),
        job_function_id:
          selectedShortTermPlan?.job_function?.[0]?.id ||
          selectedShortTermPlan?.job_function?.id ||
          selectedShortTermPlan?.job_function_id ||
          null,

        sub_job_function_ids:
          selectedShortTermPlan?.job_sub_function?.map(
            (item: any) => item.id
          ) ||
          selectedShortTermPlan?.sub_job_function_ids ||
          [],
      });

      setIsGlobal(is_global);
      setIsAllGrades(is_all_grade);

      const selectedJobGrades =
        jobGrades?.filter((jg: IJobGrade) =>
          selectedGradeIds.includes(jg.id)
        ) || [];

      const mergedCountries =
        selectedJobGrades?.flatMap(
          jg => jg.countries || []
        ) || [];

      const uniqueCountries = mergedCountries.filter(
        (c, idx, self) =>
          c &&
          idx === self.findIndex(sc => sc.id === c.id)
      );

      setGradeCountries(uniqueCountries);
    } else {
      setShortTermPlan({
        name: "",
        plan_type_id: null,
        country_ids: [],
        grade_ids: [],

        job_function_id: null,
        sub_job_function_id: null,
      });

      setIsGlobal(false);
      setIsAllGrades(false);
      setGradeCountries([]);
    }
  }, [selectedShortTermPlan, jobGrades]);

  const handleGradeChange = (selectedGradeIds: number[]) => {
    if (
      selectedGradeIds.includes(ALL_GRADES_VALUE as any)
    ) {
      const allGradeIds =
        jobGrades?.map((jg: IJobGrade) => jg.id) || [];

      const allCountries =
        jobGrades
          ?.flatMap(jg => jg.countries || [])
          .filter(
            (c, idx, self) =>
              c &&
              idx === self.findIndex(sc => sc.id === c.id)
          ) || [];

      setGradeCountries(allCountries);

      setShortTermPlan(prev => ({
        ...prev,
        grade_ids: allGradeIds,
        country_ids: [],
      }));

      return;
    }

    const selectedJobGrades =
      jobGrades?.filter((jg: IJobGrade) =>
        selectedGradeIds.includes(jg.id)
      ) || [];

    const mergedCountries =
      selectedJobGrades?.flatMap(
        jg => jg.countries || []
      ) || [];

    const uniqueCountries = mergedCountries.filter(
      (c, idx, self) =>
        c &&
        idx === self.findIndex(sc => sc.id === c.id)
    );

    setGradeCountries(uniqueCountries);

    setShortTermPlan(prev => ({
      ...prev,
      grade_ids: selectedGradeIds,
      country_ids: [],
    }));
  };

  const handleSubmit = async () => {
    try {
      const { grade_ids, country_ids } = shortTermPlan;

      const body = {
        ...shortTermPlan,
        grade_ids: isAllGrades ? [] : grade_ids,
        country_ids,
      };

      if (selectedShortTermPlan && isEdit) {
        await editShortTermPlan(body);
      } else {
        await addShortTermPlan(body);
      }

      setIsVisible(false);

      showSuccessPopup({
        title: `Short Term Incentive Plan ${isEdit ? "Updated!" : "Created!"
          }`,
        desc: `You have successfully ${isEdit ? "updated the" : "created new"
          } short term plan.`,
        role: "client",
      });
    } catch (error) {
      ErrorServices(error);
    }
  };

  const addShortTermPlan = async (body: IFormValues) =>
    await createShortTermPlan({
      company_id,
      body,
    }).unwrap();

  const editShortTermPlan = async (body: IFormValues) =>
    await updateShortTermPlan({
      company_id,
      id: selectedShortTermPlan?.id,
      body,
    }).unwrap();

  return (
    <CustomModal
      width="w-6/12"
      footer={
        <>
          <button
            className="bg-revampOrange text-white font-medium text-lg py-2 px-14 rounded-md"
            onClick={() => setIsVisible(false)}
          >
            Cancel
          </button>

          <button
            className={`bg-revampBlue text-white font-medium text-lg py-2 px-14 rounded-md ${!validation.isValid ||
              isCreating ||
              isUpdating
              ? "opacity-50"
              : ""
              }`}
            disabled={
              !validation.isValid ||
              isCreating ||
              isUpdating
            }
            onClick={handleSubmit}
          >
            {isCreating || isUpdating ? (
              <LoadingOutlined className="spinner" />
            ) : isEdit ? (
              "Update"
            ) : (
              "Create"
            )}
          </button>
        </>
      }
      title={`${isEdit ? "Update" : "Create"} a STI Plan`}
      isVisible={isVisible}
    >
      <>
        <Row justify="space-between">
          <Col span={11}>
            <label className="font-medium text-revampBlue text-lg leading-10 mt-4 ">
              Plan Name
            </label>

            <Input
              className="borderless-input w-full shadow-sm my-2 font-semibold text-sm bg-white px-3 py-3 rounded-md"
              value={shortTermPlan?.name}
              onBlur={() => validation.touch("name")}
              onChange={e =>
                setShortTermPlan(prev => ({
                  ...prev,
                  name: e?.target.value,
                }))
              }
              size="large"
              placeholder="Enter plan name here..."
            />
          </Col>

          <Col span={11}>
            <label className="font-medium text-revampBlue text-lg leading-10 mt-4 ">
              Plan Type
            </label>

            <Select
              className="custom-select"
              value={
                shortTermPlan?.plan_type_id || undefined
              }
              loading={isFetchingTypes}
              size="large"
              showSearch={false}
              showArrow
              placeholder="Select plan type from here..."
              onBlur={() => validation.touch("plan_type")}
              onChange={plan_type_id =>
                setShortTermPlan(prev => ({
                  ...prev,
                  plan_type_id,
                }))
              }
            >
              {sortedByName(planTypes)?.map(
                ({ id, name }: IPlanType) => (
                  <Option key={id} value={id}>
                    {name}
                  </Option>
                )
              )}
            </Select>
          </Col>
        </Row>

        <Row justify="space-between">
          <Col span={11}>
            <label className="font-medium text-revampBlue text-lg leading-10 mt-4">
              Grades
            </label>

            <Select
              className="custom-select"
              disabled={isAllGrades}
              loading={isFetchingGrades}
              mode="multiple"
              maxTagCount="responsive"
              maxTagPlaceholder={(omittedValues) => `+${omittedValues.length} more`}
              showArrow
              size="large"
              placeholder="Select grades from here..."
              optionLabelProp="label"
              value={shortTermPlan?.grade_ids}
              onBlur={() =>
                validation.touch("grade_ids")
              }
              onChange={handleGradeChange}
            >
              {!jobGrades
                ?.map((jg: IJobGrade) => jg.id)
                ?.every(id =>
                  shortTermPlan?.grade_ids?.includes(id)
                ) && (
                  <Option
                    key={ALL_GRADES_VALUE}
                    value={ALL_GRADES_VALUE}
                  >
                    All Grades
                  </Option>
                )}

              {jobGrades?.map((jobGrade: IJobGrade) => (
                <Option
                  key={jobGrade.id}
                  value={jobGrade.id}
                  label={`${jobGrade.grade}`}
                >
                  {jobGrade.grade}
                </Option>
              ))}
            </Select>
          </Col>

          <Col span={11}>
            <label className="font-medium text-revampBlue text-lg leading-10 mt-4 ">
              Countries
            </label>

            <Select
              className="custom-select"
              disabled={
                isGlobal ||
                shortTermPlan?.grade_ids?.length === 0
              }
              loading={isFetchingCountries}
              mode="multiple"
              maxTagCount="responsive"
              maxTagPlaceholder={(omittedValues) => `+${omittedValues.length} more`}
              size="large"
              showSearch
              showArrow
              optionFilterProp="children"
              filterOption={(input: any, option: any) =>
                option.children
                  .toLowerCase()
                  .indexOf(input.toLowerCase()) >= 0
              }
              value={shortTermPlan?.country_ids}
              onBlur={() =>
                validation.touch("countries")
              }
              onChange={(country_ids: any[]) => {
                if (country_ids.includes(ALL_VALUE)) {
                  setShortTermPlan(prev => ({
                    ...prev,
                    country_ids:
                      gradeCountries?.map(c => c.id),
                  }));
                } else {
                  setShortTermPlan(prev => ({
                    ...prev,
                    country_ids,
                  }));
                }
              }}
              placeholder="Select country from here..."
            >
              {!gradeCountries?.every(c =>
                shortTermPlan?.country_ids?.includes(c.id)
              ) && (
                  <Option key={ALL_VALUE} value={ALL_VALUE}>
                    All Countries
                  </Option>
                )}

              {sortedByName(gradeCountries)?.map(
                ({ id, name }: ICountry) => (
                  <Option key={id} value={id}>
                    {name}
                  </Option>
                )
              )}
            </Select>
          </Col>
        </Row>
        <Row justify="space-between">
          <Col span={11}>
            <label className="font-medium text-revampBlue text-lg leading-10 mt-4">
              Function
            </label>

            <Select
              className="custom-select"
              size="large"
              placeholder="Select Function..."
              value={shortTermPlan.job_function_id || undefined}
              onChange={job_function_id =>
                setShortTermPlan(prev => ({
                  ...prev,
                  job_function_id,
                  sub_job_function_ids: [],
                }))
              }
            >
              {jobFunctions.map((item: any) => (
                <Option key={item.id} value={item.id}>
                  {item.name}
                </Option>
              ))}
            </Select>
          </Col>

          <Col span={11}>
            <label className="font-medium text-revampBlue text-lg leading-10 mt-4">
              Sub-Function
            </label>

            <Select
              mode="multiple"
              maxTagCount="responsive"
              maxTagPlaceholder={(omittedValues) => `+${omittedValues.length} more`}
              className="custom-select"
              size="large"
              placeholder="Select Sub-Functions..."
              value={shortTermPlan.sub_job_function_ids}
              disabled={!shortTermPlan.job_function_id}
              onChange={(value: any[]) => {
                if (value.includes("ALL_SUB_FUNCTIONS")) {
                  setShortTermPlan(prev => ({
                    ...prev,
                    sub_job_function_ids: subJobFunctions.map(
                      (item: any) => item.id
                    ),
                  }));

                  return;
                }

                setShortTermPlan(prev => ({
                  ...prev,
                  sub_job_function_ids: value,
                }));
              }}
            >
              <Option key="ALL_SUB_FUNCTIONS" value="ALL_SUB_FUNCTIONS">
                All Sub-Functions
              </Option>

              {subJobFunctions.map((item: any) => (
                <Option key={item.id} value={item.id}>
                  {item.name}
                </Option>
              ))}
            </Select>
          </Col>
        </Row>
      </>
    </CustomModal>
  );
};

export default AddShortTermPlan;